import * as Sentry from '@sentry/react'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { getInstallationMethod, getPWABrowserCategory, isEdge, isInStandaloneMode, isUnsupportedPWABrowser, supportsPWAManualInstall } from '../utils/helpers'

// Global error handler for Edge PWA to prevent native alerts
const setupEdgePWAErrorHandling = () => {
	const isEdgePWA = isEdge() && isInStandaloneMode()

	if (isEdgePWA) {
		// Override window.alert, window.confirm, window.prompt to prevent native dialogs
		const originalAlert = window.alert
		const originalConfirm = window.confirm
		const originalPrompt = window.prompt

		window.alert = () => {
			return undefined
		}

		window.confirm = () => {
			return true // Default to true to not break functionality
		}

		window.prompt = (message?: string, defaultText?: string) => {
			return defaultText || null
		}

		// Add global error handler
		window.addEventListener('error', (event) => {
			event.preventDefault()
			return true
		})

		window.addEventListener('unhandledrejection', (event) => {
			event.preventDefault()
		})

		// Return cleanup function
		return () => {
			window.alert = originalAlert
			window.confirm = originalConfirm
			window.prompt = originalPrompt
		}
	}

	return () => {} // No cleanup needed for non-Edge PWA
}

interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface PWAContextType {
	// State
	isInstallable: boolean
	isInstalling: boolean
	isInstalled: boolean
	isReady: boolean
	installPrompt: BeforeInstallPromptEvent | null
	supportsManualInstall: boolean
	isUnsupportedBrowser: boolean
	installationMethod: 'automatic' | 'manual' | 'unsupported'
	browserCategory: 'supported-automatic' | 'supported-manual' | 'unsupported'

	// Actions
	triggerInstall: () => Promise<boolean>
	openInstalledApp: () => void
	dismissPrompt: () => void
	resetInstallState: () => void
}

const PWAContext = createContext<PWAContextType | undefined>(undefined)

export const PWAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
	const [isInstalling, setIsInstalling] = useState(false)
	const [isInstalled, setIsInstalled] = useState(false)
	const [isReady, setIsReady] = useState(false)

	// Check if app is installable (has prompt and not in standalone mode)
	const isInstallable = !!installPrompt && !isInStandaloneMode() && !isInstalled

	// Check if browser supports manual installation
	const supportsManualInstall = supportsPWAManualInstall() && !isInStandaloneMode() && !isInstalled

	// Check if browser is unsupported
	const isUnsupportedBrowser = isUnsupportedPWABrowser()

	// Get installation method for current browser
	const installationMethod = getInstallationMethod()

	// Get browser category
	const browserCategory = getPWABrowserCategory()

	// Listen for the beforeinstallprompt event
	useEffect(() => {
		const handleBeforeInstallPrompt = (event: Event) => {
			try {
				// Prevent the mini-infobar from appearing on mobile
				event.preventDefault()

				// Save the event so it can be triggered later
				setInstallPrompt(event as BeforeInstallPromptEvent)
			} catch (error) {
				// Silently handle any errors to prevent native alerts
			}
		}

		const handleAppInstalled = () => {
			try {
				// App was installed successfully
				setIsInstalled(true)
				setInstallPrompt(null)
				setIsInstalling(false)
			} catch (error) {
				// Silently handle any errors to prevent native alerts
			}
		}

		// Check if already in standalone mode (already installed)
		const checkStandaloneMode = () => {
			try {
				const isStandalone = isInStandaloneMode()

				if (isStandalone) {
					setIsInstalled(true)
				}
			} catch (error) {
				// Silently handle any errors to prevent native alerts
			}
		}

		try {
			// Setup Edge PWA error handling
			const cleanupEdgeErrorHandling = setupEdgePWAErrorHandling()

			// Add event listeners only if they exist (avoid issues on unsupported browsers)
			if (typeof window !== 'undefined' && 'addEventListener' in window) {
				window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
				window.addEventListener('appinstalled', handleAppInstalled)
			}

			// Initial check
			checkStandaloneMode()

			// Set ready state after initial setup
			setTimeout(() => {
				setIsReady(true)
			}, 100) // Small delay to allow for event listeners to be set up

			// Store cleanup function for later use
			return cleanupEdgeErrorHandling
		} catch (error) {
			// Silently handle any errors to prevent native alerts
			setIsReady(true) // Set ready even on error
		}

		// Cleanup
		return () => {
			try {
				if (typeof window !== 'undefined' && 'removeEventListener' in window) {
					window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
					window.removeEventListener('appinstalled', handleAppInstalled)
				}
			} catch (error) {
				// Silently handle any errors to prevent native alerts
			}
		}
	}, [])

	// Trigger the installation prompt
	const triggerInstall = useCallback(async (): Promise<boolean> => {
		if (!installPrompt || isInstalling) {
			return false
		}

		try {
			setIsInstalling(true)

			// Show the installation prompt - this should NOT trigger any native alerts
			await installPrompt.prompt()

			// Wait for the user's choice with timeout for Edge
			const choicePromise = installPrompt.userChoice
			const timeoutPromise = new Promise((_, reject) => {
				setTimeout(() => reject(new Error('Installation prompt timeout')), 10000)
			})

			const choiceResult = (await Promise.race([choicePromise, timeoutPromise])) as any

			if (choiceResult.outcome === 'accepted') {
				// User accepted the installation
				setIsInstalled(true)
				setInstallPrompt(null)
				return true
			}
			// User dismissed the installation
			setInstallPrompt(null)
			return false
		} catch (error) {
			// Enhanced error handling for Edge PWA - suppress all native alerts
			const isEdgePWA = isEdge() && isInStandaloneMode()

			if (isEdgePWA) {
				// Don't send to Sentry for Edge PWA to avoid noise
			} else {
				Sentry.captureException(error)
			}

			return false
		} finally {
			setIsInstalling(false)
		}
	}, [installPrompt, isInstalling])

	// Open the installed app (redirect to app URL)
	const openInstalledApp = useCallback(() => {
		try {
			// Try to open the app in standalone mode
			if (isInStandaloneMode()) {
				// Already in standalone mode, just refresh or navigate to main page
				window.location.href = '/'
			} else {
				// Try to open in standalone mode by navigating to the app URL
				window.open('/', '_blank')
			}
		} catch (error) {
			// Silently handle any errors to prevent native alerts
		}
	}, [])

	// Dismiss the prompt without installing
	const dismissPrompt = useCallback(() => {
		setInstallPrompt(null)
	}, [])

	// Reset installation state (useful for testing)
	const resetInstallState = useCallback(() => {
		setIsInstalled(false)
		setIsInstalling(false)
		setInstallPrompt(null)
	}, [])

	const contextValue: PWAContextType = {
		// State
		isInstallable,
		isInstalling,
		isInstalled,
		isReady,
		installPrompt,
		supportsManualInstall,
		isUnsupportedBrowser,
		installationMethod,
		browserCategory,

		// Actions
		triggerInstall,
		openInstalledApp,
		dismissPrompt,
		resetInstallState
	}

	return <PWAContext.Provider value={contextValue}>{children}</PWAContext.Provider>
}

export const usePWAContext = (): PWAContextType => {
	const context = useContext(PWAContext)
	if (context === undefined) {
		throw new Error('usePWAContext must be used within a PWAProvider')
	}
	return context
}
